import React from "react";
import { Typography, IconButton, Button, makeStyles } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import StarIcon from "@material-ui/icons/Star";
import CustomGrid from "../../../../components/src/CustomGrid.web";

const useStyles = makeStyles(theme => ({
  card: {
    display: "flex",
    flexDirection: "row",
    gap: 16,
    padding: 12,
    background: "#fff",
    borderRadius: 8,
    boxShadow:
      "rgba(12,12,13,0.1) 0px 16px 16px, rgba(12,12,13,0.05) 0px 4px 2px",
    width: "100%",
    height: "100%",
    minHeight: 156,
    boxSizing: "border-box"
  },
  iconBox: {
    width: 70,
    height: 70,
    flexShrink: 0,
    backgroundColor: "#f3f7f7",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden"
  },
  image: {
    width: 30,
    height: 30,
    objectFit: "contain",
    opacity: 0.7
  },
  icon: {
    color: theme.palette.primary.main,
    opacity: 0.7,
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden"
  },
  topSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minHeight: 0,
    overflow: "hidden"
  },
  bottomSection: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  name: {
    fontSize: 19,
    fontWeight: 600,
    color: theme.palette.primary.main,
    lineHeight: 1,
    margin: 0
  },
  description: {
    fontSize: 12,
    fontWeight: 400,
    color: "#666",
    lineHeight: 1.4,
    margin: 0,
    overflow: "hidden"
  },
  priceRow: {
    display: "flex",
    alignItems: "center",
    gap: 4
  },
  priceAmount: {
    fontSize: 19,
    fontWeight: 600,
    color: theme.palette.primary.main,
    lineHeight: 1
  },
  perUnit: {
    fontSize: 14,
    fontWeight: 400,
    color: "#666",
    lineHeight: 1.4
  },
  selectRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 4
  }
}));

type PackageGridCardProps = {
  image: string | React.ComponentType<{ size?: number; className?: string }>;
  name: string;
  description?: string;
  price: string;
  isRecommended?: boolean;
  cardClassName?: string;
  isDisabled?: boolean;
  onSelect?: () => void;
  onDetail?: () => void;
  classes?: Record<string, string>;
};

const PackageGridCard = ({
  image,
  name,
  description,
  price,
  isRecommended,
  cardClassName,
  isDisabled,
  onSelect,
  onDetail,
  classes: classesProp
}: PackageGridCardProps) => {
  const ownClasses = useStyles();
  const classes = { ...ownClasses, ...classesProp };
  const clampedDescription =
    description && description.length > 140
      ? description.slice(0, 137).trimEnd() + "…"
      : description;
  const IconComponent = typeof image !== "string" ? image : null;
  const imageSrc = typeof image === "string" ? image : undefined;

  return (
    <CustomGrid item xs={12} sm={6} md={4}>
      <div className={[classes.card, cardClassName].filter(Boolean).join(" ")}>
        <div className={classes.iconBox}>
          {IconComponent
            ? <IconComponent size={30} className={classes.icon} />
            : <img className={classes.image} src={imageSrc} alt={name} />}
        </div>
        <div className={classes.content}>
          <div className={classes.topSection}>
            <Typography className={classes.name}>{name}</Typography>
            <Typography className={classes.description}>
              {clampedDescription}
            </Typography>
          </div>
          <div className={classes.bottomSection}>
            <div className={classes.priceRow}>
              <Typography className={classes.priceAmount}>{price}</Typography>
              <Typography className={classes.perUnit}>per unit</Typography>
            </div>
            <div className={classes.selectRow}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={isDisabled}
                onClick={onSelect}
                style={{ width: 75, height: 34, fontSize: 12, borderRadius: 4 }}
                id="selectPackage"
              >
                Select
              </Button>
              <IconButton
                disableFocusRipple
                disableTouchRipple
                disableRipple
                id="dialogDetail"
                onClick={onDetail}
                aria-label="info"
              >
                <InfoOutlinedIcon
                  style={{ color: "#54A4A6", width: "1.2em", height: "1.2em" }}
                />
              </IconButton>
            </div>
            {isRecommended && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <StarIcon style={{ color: "red", marginRight: 8 }} />
                <Typography
                  style={{
                    color: "red",
                    fontWeight: "bold",
                    fontSize: "0.8rem"
                  }}
                >
                  Recommended
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </CustomGrid>
  );
};

export default PackageGridCard;
